import { makeAutoObservable } from 'mobx';
import { KbNode, UserAccess, getKbTreeData } from '@/services/kbService';
import { extIconsMap } from '@/common/file-utils';

const findTreeNodeChain = (
  id: string,
  chain: KbNode[],
  tree?: KbNode[]
): KbNode[] => {
  if (!tree) {
    return [];
  }
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    if (node.uuid === id) {
      chain.push(node);
      return chain;
    } else {
      let newChain = findTreeNodeChain(id, [...chain, node], node.children);
      if (newChain[newChain.length - 1]?.uuid === id) {
        return newChain;
      }
    }
  }
  return [];
};

const getTreeNodeChain = (id: string, node?: KbNode[]): KbNode[] => {
  return findTreeNodeChain(id, [], node);
};

const getTreeNodeAllChildren = (treeNode: KbNode) => {
  const children: KbNode[] = [];

  const stack: KbNode[] = [];
  stack.push(...treeNode.children);

  while (stack.length) {
    const node = stack.shift();
    if (node.access === UserAccess.Writable && node.file_id) {
      children.push(node);
    }
    if (node.children) {
      stack.push(...node.children);
    }
  }
  return children;
};

const findTreeNodes = (uuids: string[], treeData: KbNode[]): KbNode[] => {
  const stack = [...treeData]
  const res: KbNode[] = []
  while (stack.length) {
    const node = stack.shift()
    if (uuids.includes(node?.uuid)) {
      res.push(node)
    }
    stack.push(...(node?.children ?? []))
  }
  return res
}


export class KbStore {
  selectedKbIds: string[] = [];

  expandedKbIds: string[] = []

  kbTreeData: KbNode[] = [];

  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  updateLoading(val = false) {
    this.loading = val;
  }

  updateTreeData(treeData: KbNode[]) {
    this.kbTreeData = treeData;
  }

  async fetchTreeData(query?: {kbId: string, containChildren: string}) {
    this.updateLoading(true);
    try {
      const { errno, data } = await getKbTreeData();
      this.updateLoading(false);
      if (errno) {
        return;
      }
      this.updateTreeData(data.tree);
      if (!query || !query.kbId) {
        return;
      }
      console.log('>>> url query are ', query)
      const nodeChain = getTreeNodeChain(query.kbId, data.tree);
      const parentIds = nodeChain.map(node => node.uuid);
      let checkedIds: string[] = [];
      let expandedIds: string[] = []
      const node = nodeChain[nodeChain.length - 1];
      if (query.containChildren === '1') {
        const children = getTreeNodeAllChildren(node);
        const childrenIds = children.map(child => child.uuid);
        if (node.access === UserAccess.Writable && node.file_id) {
          checkedIds.push(query.kbId)
        }
        checkedIds.push(...childrenIds);
        expandedIds = [...parentIds, ...childrenIds]
      } else {
        if (node.access === UserAccess.Writable && node.file_id) {
          checkedIds.push(query.kbId)
        }
        expandedIds = parentIds
      }
      this.updateSelectedKbIds(checkedIds)
      this.updateExpandedKbIds(expandedIds)
    } catch (e) {
      console.error('failed to fetch tree', e);
      this.updateLoading(false);
    }
  }

  updateSelectedKbIds(ids: string[]) {
    this.selectedKbIds = ids;
  }

  updateExpandedKbIds(ids: string[]) {
    this.expandedKbIds = ids
  }

  getOutput() {
    const { selectedKbIds, kbTreeData } = this
    const nodes = findTreeNodes(selectedKbIds, kbTreeData)
    return nodes.reduce((acc, node) => {
      acc[node.file_id] = {
        business_type: 0,
        category_info: 0,
        description: '',
        folder_id: node.file_id,
        is_default: 0,
        is_square_content: '',
        ks_id: '',
        logo: extIconsMap[node.file_ext] ?? extIconsMap.unknown,
        member_num: '',
        name: node.title,
        role: ''
      }
      return acc
    }, {} as Record<string, any>)
  }
}

const store = new KbStore();

export default store;
